import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  EditOutlined,
  HomeOutlined,
  LinkedinOutlined,
  LoginOutlined,
  ShareAltOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Input, Layout, Result, Row, Space, Typography, message } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { Whatsapp } from "@/Assets/Index";
import AppLayout from "@/Components/Common/AppLayout";
import { UserContext } from "@/Context/UserContext";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const ThankYou = () => {
  const navigate = useNavigate();
  const { resumeId } = useParams();
  const { userProfileData } = useContext(UserContext);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [resumeData, setResumeData] = useState(null);
  const [reviewText, setReviewText] = useState("");
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const fetch = useAuthenticatedFetch();

  useEffect(() => {
    // Check if user is logged in
    const userId = localStorage.getItem("userId");
    const authToken = localStorage.getItem("authorization");
    setIsLoggedIn(!!(userId && authToken) || !!userProfileData?.email);

    // Track thank you page visit
    const trackThankYouVisit = async () => {
      if (resumeId) {
        try {
          await fetch.post(`/resume/${resumeId}/track-thank-you`);
        } catch (error) {
          console.error("Error tracking thank you page visit", error.message || error);
        }
      }
    };

    // Fetch current resume data for autofill functionality
    const fetchResumeData = async () => {
      if (resumeId) {
        try {
          const response = await fetch.get(`/resume/${resumeId}`);

          if (response.success && response.data) {
            setResumeData(response.data);
          } else {
          }
        } catch (error) {
          console.error("Error fetching resume data for autofill", error.message || error);
        }
      } else {
      }
    };

    // Track the visit first, then fetch data
    trackThankYouVisit();
    fetchResumeData();
  }, [userProfileData]);

  const handleGoHome = () => {
    // Navigate to the static home page instead of React route
    window.location.href = "/";
  };

  const handleCreateAnother = () => {
    window.location.href = "/app/resume-upload";
  };

  const handleLinkedInShare = () => {
    const shareUrl = "https://www.upresume.ai/"; // Live production URL
    const shareText = `Recently I updated my resume in under 10 minutes — for FREE.
No sign-ups. No paywalls. Just results. 🚀

I've tried a lot of resume tools before, but this one honestly felt different. Here's why:
✅ AI analysis that shows strengths & areas for improvement
✅ Helpful tips every time you enhance a section — and they actually get better as you go
✅ Freedom to edit any field manually, so I stayed in control
✅ Clean, ATS-friendly templates ready to download instantly
✅ And yes — all of this without even creating an account

For me, the best part was seeing exactly what needed improvement and being able to fix it right away. It felt like having a career coach built into the tool.

💼 If you're preparing for job applications or just want to polish your CV, I highly recommend giving it a try.
👉 ${shareUrl}

Big thanks to the team behind this — making job hunting just a little easier for all of us. 🙌

#JobSearch #CareerAdvice #InterviewTips #Hiring #Jobs #Careers #JobInterviews #ResumeTips #ResumeWriting #JobSeekers #CareerGrowth #JobHunting #AItools #FutureOfWork #OpenToWork`;

    const linkedInUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(shareText)}`;
    window.open(linkedInUrl, "_blank", "width=600,height=400");
  };

  const handleWhatsAppShare = () => {
    const shareUrl = "https://www.upresume.ai/"; // Live production URL
    const shareText = `Recently I updated my resume in under 10 minutes — for FREE.
No sign-ups. No paywalls. Just results. 🚀

I've tried a lot of resume tools before, but this one honestly felt different. Here's why:
✅ AI analysis that shows strengths & areas for improvement
✅ Helpful tips every time you enhance a section — and they actually get better as you go
✅ Freedom to edit any field manually, so I stayed in control
✅ Clean, ATS-friendly templates ready to download instantly
✅ And yes — all of this without even creating an account

For me, the best part was seeing exactly what needed improvement and being able to fix it right away. It felt like having a career coach built into the tool.

💼 If you're preparing for job applications or just want to polish your CV, I highly recommend giving it a try.
👉 ${shareUrl}

Big thanks to the team behind this — making job hunting just a little easier for all of us. 🙌`;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(whatsappUrl, "_blank", "width=600,height=400");
  };

  const handleLogin = () => {
    // Check if already authenticated
    const userId = localStorage.getItem("userId");
    const authToken = localStorage.getItem("authorization");
    if (userId && authToken) {
      navigate("/app/dashboard");
      return;
    }

    navigate("/app/login", {
      state: {
        resumeData: resumeData,
      },
    });
  };

  const handleSignup = () => {
    // Check if already authenticated
    const userId = localStorage.getItem("userId");
    const authToken = localStorage.getItem("authorization");
    if (userId && authToken) {
      navigate("/app/dashboard");
      return;
    }

    navigate("/app/signup", {
      state: {
        resumeData: resumeData,
      },
    });
  };

  const handleBack = () => {
    navigate("/app/dashboard");
  };

  const handleSubmitReview = async () => {
    if (!reviewText.trim()) {
      message.warning("Please enter your review before submitting.");
      return;
    }

    setIsSubmittingReview(true);
    try {
      const response = await fetch.post(`/resume/${resumeId}/submit-review`, {
        review: reviewText.trim(),
      });

      if (response.success) {
        message.success("Thank you for your review! We appreciate your feedback.");
        setReviewText("");
      } else {
        message.error("Failed to submit review. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting review", error.message || error);
      message.error("Failed to submit review. Please try again.");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  return (
    <AppLayout>
      <style>{`
        /* CSS Reset to prevent page stretching and maintain sticky positioning */
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          height: 100% !important;
          width: 100% !important;
          overflow-x: clip !important;
          overflow-y: auto !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
        }

        *, *::before, *::after {
          box-sizing: border-box !important;
        }

        .ant-layout, .ant-layout-content {
          margin: 0 !important;
          padding: 0 !important;
          position: relative !important;
        }

        .ant-layout {
          overflow: visible !important;
        }

        body {
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
        }
      `}</style>
      <div
        style={{
          padding: "20px",
          minHeight: "100vh",
          backgroundColor: "#fafafa",
          position: "relative",
        }}
      >
        {/* Header with back button */}
        <div
          style={{
            position: "absolute",
            top: "20px",
            left: "80px",
            zIndex: 10,
          }}
        >
          <Button
            type="default"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="back-to-options-btn"
            style={{
              fontSize: "14px",
              borderColor: "#1890ff",
              color: "#1890ff",
              backgroundColor: "transparent",
              height: "36px",
              paddingLeft: "16px",
              paddingRight: "16px",
              borderRadius: "6px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.3s ease",
            }}
          >
            Dashboard
          </Button>
        </div>

        {/* Main content container centered */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: "calc(100vh - 140px)",
            paddingTop: "80px",
          }}
        >
          <Row gutter={[32, 32]} style={{ width: "100%", maxWidth: "1200px", justifyContent: "center" }}>
            {/* Left Column - Thank You Section */}
            <Col xs={24} md={12}>
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                  padding: "40px",
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {/* Thank You Header */}
                <div style={{ marginBottom: "32px" }}>
                  <div style={{ textAlign: "center", marginBottom: "16px" }}>
                    <CheckCircleOutlined style={{ color: "#1890ff", fontSize: "48px" }} />
                  </div>
                  <Title level={2} style={{ color: "#1890ff", marginBottom: "16px", fontSize: "24px" }}>
                    Thank you for using Upresume.ai!
                  </Title>
                  <Text
                    style={{
                      color: "#8c8c8c",
                      fontSize: "14px",
                      display: "block",
                    }}
                  >
                    Best of luck with your job applications!
                  </Text>
                </div>

                {/* Save Progress Section - Only for non-logged in users */}
                {!isLoggedIn && (
                  <div
                    style={{
                      backgroundColor: "#f8fafc",
                      padding: "24px",
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      textAlign: "center",
                      marginBottom: "40px",
                    }}
                  >
                    <Title level={4} style={{ color: "#1e293b", marginBottom: "12px" }}>
                      💡 Save Your Progress
                    </Title>
                    <Text style={{ color: "#64748b", fontSize: "14px", display: "block", marginBottom: "8px" }}>
                      Don't lose your work! Create an account to:
                    </Text>
                    <div style={{ color: "#64748b", fontSize: "13px", marginBottom: "16px", textAlign: "center" }}>
                      <div style={{ marginBottom: "4px" }}>• Edit your resume anytime</div>
                      <div style={{ marginBottom: "4px" }}>• Keep your data secure</div>
                      <div>• Access from anywhere</div>
                    </div>
                    <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                      <Button
                        type="primary"
                        icon={<UserAddOutlined />}
                        onClick={handleSignup}
                        style={{
                          backgroundColor: "#1890ff",
                          borderColor: "#1890ff",
                          borderRadius: "6px",
                          minWidth: "120px",
                        }}
                      >
                        Sign Up Free
                      </Button>
                      <Button
                        icon={<LoginOutlined />}
                        onClick={handleLogin}
                        style={{
                          borderRadius: "6px",
                          minWidth: "120px",
                        }}
                      >
                        Login
                      </Button>
                    </div> 
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    icon={<HomeOutlined />}
                    size="large"
                    onClick={handleGoHome}
                    style={{
                      borderRadius: "8px",
                      height: "48px",
                      fontSize: "16px",
                      minWidth: "200px",
                    }}
                  >
                    Back to Home
                  </Button>
                </div>
              </div>
            </Col>

            {/* Right Column - Reviews Section */}
            <Col xs={24} md={12}>
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                  padding: "40px",
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Reviews Header */}
                <div style={{ marginBottom: "0px" }}>
                  <div style={{ textAlign: "center", marginBottom: "8px" }}>
                    <span style={{ fontSize: "48px" }}>⭐</span>
                  </div>
                  <Title level={2} style={{ color: "#1890ff", marginBottom: "16px", fontSize: "24px" }}>
                    Please add your reviews...
                  </Title>
                  <Text
                    style={{
                      color: "#8c8c8c",
                      fontSize: "14px",
                      display: "block",
                      marginBottom: "40px",
                    }}
                  >
                    We'd love to hear about your experience with Upresume.ai!
                  </Text>
                </div>

                <TextArea
                  rows={6}
                  placeholder="Share your thoughts, feedback, or suggestions..."
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  style={{
                    marginBottom: "40px",
                    borderRadius: "8px",
                    width: "100%",
                    backgroundColor: "#f8fafc",
                  }}
                  maxLength={1000}
                  showCount
                />

                {/* Action Buttons */}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    size="large"
                    onClick={handleSubmitReview}
                    loading={isSubmittingReview}
                    style={{
                      borderRadius: "8px",
                      height: "48px",
                      fontSize: "16px",
                      minWidth: "200px",
                    }}
                  >
                    Submit Review
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </AppLayout>
  );
};

export default ThankYou;
